"use client";

// Server-driven client behavior config (v1.6) — the web half of the
// no-release escape hatch. Mirrors server/client_config.py.
//
// Contract (see design/v1_6_master_plan.md §client-config):
//   1. Hardcoded DEFAULT_CLIENT_CONFIG = today's behavior. The fetch only
//      OVERRIDES. A client that never reaches the server behaves as shipped.
//   2. Cached in localStorage, refreshed on mount. Reads NEVER block on the
//      network — getClientConfig() is synchronous (cache-or-default).
//   3. Behavior only (feature kill-switches, timeouts, retry policy). Never UI
//      layout; never gates offline capability.
//   4. Fail-safe: an absent feature flag (or an unreachable server) means the
//      flag is treated as enabled = today's behavior. Set a flag false ONLY to
//      disable a regressed fix server-side without a redeploy of the client.

import type { ClientConfig } from "@/app/lib/types";
import { fetchClientConfig } from "@/app/lib/api";

export const DEFAULT_CLIENT_CONFIG: ClientConfig = {
  config_version: 1,
  features: {
    auto_judge: true,
    voice_mode: true,
    tts: true,
    stream_auto_resume: true,
    fenced_code_block_render: true,
    tts_transport_bar_persist: true,
    lockscreen_duration_fix: true,
    scroll_anchor_fix: true,
    card_snap_delta_fix: true,
    offline_folder_create: true,
    read_aloud_cached_icon: true,
    tts_airplane_replay: true,
  },
  timeouts: {
    auto_judge_grace_sec: 12,
    auto_judge_client_timeout_sec: 300,
    stream_idle_timeout_sec: 90,
    stream_resume_poll_sec: 30,
    session_poll_sec: 5,
  },
  limits: { max_concurrent_sends: 8 },
  retry: { show_retry_only_on_server_failure: true },
  providers_disabled: [],
};

// Intentionally unwired on web: voice_mode has no web counterpart; and
// card_snap_delta_fix, tts_transport_bar_persist, lockscreen_duration_fix,
// scroll_anchor_fix, read_aloud_cached_icon, tts_airplane_replay,
// offline_folder_create, and fenced_code_block_render describe iOS-specific
// behavior. Do not map them to superficially similar web UI without a genuine
// equivalent code path.

const CACHE_KEY = "trio_client_config";
let _memo: ClientConfig | null = null;

function _merge(raw: unknown): ClientConfig {
  if (!raw || typeof raw !== "object") return DEFAULT_CLIENT_CONFIG;
  const r = raw as Partial<ClientConfig>;
  return {
    config_version:
      typeof r.config_version === "number"
        ? r.config_version
        : DEFAULT_CLIENT_CONFIG.config_version,
    features: { ...DEFAULT_CLIENT_CONFIG.features, ...(r.features || {}) },
    timeouts: { ...DEFAULT_CLIENT_CONFIG.timeouts, ...(r.timeouts || {}) },
    limits: { ...DEFAULT_CLIENT_CONFIG.limits, ...(r.limits || {}) },
    retry: { ...DEFAULT_CLIENT_CONFIG.retry, ...(r.retry || {}) },
    providers_disabled: Array.isArray(r.providers_disabled)
      ? r.providers_disabled
      : [],
  };
}

/** Synchronous: in-memory memo → localStorage cache → shipped defaults.
 *  Never throws, never blocks. Safe to call during render. */
export function getClientConfig(): ClientConfig {
  if (_memo) return _memo;
  if (typeof window === "undefined") return DEFAULT_CLIENT_CONFIG;
  try {
    const cached = window.localStorage.getItem(CACHE_KEY);
    if (cached) {
      _memo = _merge(JSON.parse(cached));
      return _memo;
    }
  } catch {
    /* corrupt cache — fall back to defaults */
  }
  return DEFAULT_CLIENT_CONFIG;
}

/** Fetch the latest config and cache it. Best-effort: on failure the cached /
 *  default config stays in effect. Returns whether a new config arrived. */
export async function refreshClientConfig(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const raw = await fetchClientConfig();
    _memo = _merge(raw);
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify(raw));
    } catch {
      /* quota / private mode — memo still updated for this session */
    }
    return true;
  } catch (err) {
    console.warn("[client-config] refresh failed; using cache/defaults:", err);
    return false;
  }
}

/** Fail-safe feature check: unknown/absent flag → enabled (today's behavior). */
export function isFeatureEnabled(name: string): boolean {
  const v = getClientConfig().features[name];
  return v === undefined ? true : v;
}
